import { useEffect } from "react";

/**
 * Sets per-page SEO metadata in <head>: <title>, meta description,
 * Open Graph (title/description/url), and canonical link.
 * Restores previous values on unmount so SPA navigation is clean.
 */
const upsertMeta = (selector, attrName, attrValue, content) => {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, prev, created };
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, prev, created };
};

export const useSeo = ({ title, description, ogTitle, ogDescription, ogUrl, canonical }) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const records = [];
    records.push(upsertMeta('meta[name="description"]', "name", "description", description));
    records.push(upsertMeta('meta[property="og:title"]', "property", "og:title", ogTitle));
    records.push(upsertMeta('meta[property="og:description"]', "property", "og:description", ogDescription));
    records.push(upsertMeta('meta[property="og:url"]', "property", "og:url", ogUrl));
    const canonicalRecord = upsertCanonical(canonical);

    return () => {
      document.title = previousTitle;
      records.forEach(({ el, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
      if (canonicalRecord.created) canonicalRecord.el.remove();
      else if (canonicalRecord.prev !== null) canonicalRecord.el.setAttribute("href", canonicalRecord.prev);
    };
  }, [title, description, ogTitle, ogDescription, ogUrl, canonical]);
};
